import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';

export enum ActiveIntervalUnit {
  HOURS = 'hours',
  DAYS = 'days',
}

const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_DAY = 86400;

interface ActiveIntervalFieldProps {
  value?: number; // value in seconds
  onChange: (newValue?: number) => void;
  disabled?: boolean;
  label?: string;
}

const ActiveIntervalField = (props: ActiveIntervalFieldProps) => {
  const classes = useStyles();

  const getInitialUnit = (): ActiveIntervalUnit => {
    if (props.value && props.value % SECONDS_PER_DAY === 0) {
      return ActiveIntervalUnit.DAYS;
    }
    return ActiveIntervalUnit.HOURS;
  };

  const getInitialAmount = (): string => {
    if (!props.value) return '';
    if (props.value % SECONDS_PER_DAY === 0) {
      return (props.value / SECONDS_PER_DAY).toString();
    }
    return (props.value / SECONDS_PER_HOUR).toString();
  };

  const [amount, setAmount] = useState<string>(getInitialAmount());
  const [unit, setUnit] = useState<ActiveIntervalUnit>(getInitialUnit());

  useEffect(() => {
    if (props.value === undefined) {
      setAmount('');
    }
  }, [props.value]);

  useEffect(() => {
    if (amount === '' || Number.isNaN(parseInt(amount, 10))) {
      props.onChange(undefined);
      return;
    }

    const numericAmount = parseInt(amount, 10);
    const seconds =
      unit === ActiveIntervalUnit.DAYS
        ? numericAmount * SECONDS_PER_DAY
        : numericAmount * SECONDS_PER_HOUR;

    props.onChange(seconds);
  }, [amount, unit]);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow only positive integers
    if (value === '' || /^[1-9]\d*$/.test(value)) {
      setAmount(value);
    }
  };

  const handleUnitChange = (e: SelectChangeEvent<ActiveIntervalUnit>) => {
    setUnit(e.target.value as ActiveIntervalUnit);
  };

  return (
    <Box component='div' className={classes.container}>
      <TextField
        label={props.label ?? 'Active Interval'}
        variant='outlined'
        size='small'
        value={amount}
        onChange={handleAmountChange}
        disabled={props.disabled}
        className={classes.amountField}
        type='number'
        inputProps={{ min: 1 }}
      />
      <FormControl size='small' className={classes.unitSelect}>
        <InputLabel id='active-interval-unit-label'>Unit</InputLabel>
        <Select
          labelId='active-interval-unit-label'
          value={unit}
          onChange={handleUnitChange}
          label='Unit'
          disabled={props.disabled}
        >
          <MenuItem value={ActiveIntervalUnit.HOURS}>Hours</MenuItem>
          <MenuItem value={ActiveIntervalUnit.DAYS}>Days</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
};

const useStyles = makeStyles((theme) => {
  return createStyles({
    container: {
      display: 'flex',
      flexDirection: 'row',
      gap: theme.spacing(1.5),
    },
    amountField: {
      width: 150,
    },
    unitSelect: {
      width: 120,
    },
  });
});

export default ActiveIntervalField;
